import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getTenantStats, getTenants, runJobs, impersonate } from '../../services/api';
import { safeParseFeatures } from '../../context/AuthContext';

const FEAT_ICONS = { appointments:'📅', billing:'💰', notifications:'🔔', google_calendar:'📆', whatsapp:'💬' };

function ImpersonateBtn({ tenantId }) {
  const [loading, setLoading] = useState(false);
  const handle = async () => {
    setLoading(true);
    try {
      const res = await impersonate(tenantId);
      if (res.data.token) localStorage.setItem('master_token', res.data.token);
      window.location.href = '/app';
    } catch { toast.error('Erro ao acessar cliente'); setLoading(false); }
  };
  return (
    <button className={`btn btn-xs btn-primary${loading?' btn-loading':''}`} onClick={handle} disabled={loading}>
      {loading ? '' : '🔍 Acessar'}
    </button>
  );
}

export default function MasterDashboard() {
  const [stats,   setStats]   = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningJobs, setRunningJobs] = useState(false);

  useEffect(() => {
    Promise.all([getTenantStats(), getTenants()])
      .then(([s, t]) => { setStats(s.data); setTenants(t.data.tenants.slice(0, 6)); })
      .catch(() => toast.error('Erro ao carregar dados'))
      .finally(() => setLoading(false));
  }, []);

  const handleRunJobs = async () => {
    setRunningJobs(true);
    try { await runJobs(); toast.success('Jobs executados com sucesso!'); }
    catch { toast.error('Erro ao executar jobs'); }
    finally { setRunningJobs(false); }
  };

  if (loading) return <div className="page-body" style={{ display:'flex', justifyContent:'center', paddingTop:60 }}><div className="spinner" /></div>;

  const fmt = n => `R$ ${parseFloat(n||0).toLocaleString('pt-BR',{minimumFractionDigits:2})}`;

  return (
    <div className="page-body">
      <div className="page-header-row">
        <div className="page-header">
          <h2>Dashboard Master</h2>
          <p>Visão geral de todos os clientes</p>
        </div>
        <div className="page-actions">
          <button className={`btn btn-outline btn-sm${runningJobs?' btn-loading':''}`} onClick={handleRunJobs} disabled={runningJobs}>
            ⚙️ {runningJobs ? 'Executando...' : 'Executar Jobs'}
          </button>
          <Link to="/master/tenants/new" className="btn btn-primary">+ Novo Cliente</Link>
        </div>
      </div>

      <div className="stats-grid">
        {[
          { icon:'🏢', label:'Total Clientes',   value: stats?.total_tenants      ?? 0, color:'blue'   },
          { icon:'✅', label:'Ativos',            value: stats?.active_tenants     ?? 0, color:'green'  },
          { icon:'👥', label:'Usuários Ativos',   value: stats?.total_users        ?? 0, color:'indigo' },
          { icon:'📅', label:'Agendamentos Hoje', value: stats?.appointments_today ?? 0, color:'amber'  },
          { icon:'💰', label:'Receita Mensal',    value: fmt(stats?.monthly_revenue),  color:'green', small:true },
          { icon:'⏳', label:'A Receber',         value: fmt(stats?.pending_revenue),  color:'amber', small:true },
        ].map(c => (
          <div key={c.label} className="stat-card">
            <div className={`stat-icon ${c.color}`}>{c.icon}</div>
            <div>
              <div className="stat-label">{c.label}</div>
              <div className="stat-value" style={c.small?{fontSize:16}:{}}>{c.value}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">Últimos Clientes</span>
          <Link to="/master/tenants" className="btn btn-sm btn-secondary">Ver todos →</Link>
        </div>
        <div className="card-body" style={{ padding:0 }}>
          {tenants.length === 0 ? (
            <div className="empty-state" style={{ padding:32 }}>
              <div className="empty-icon">🏢</div>
              <h3>Nenhum cliente cadastrado</h3>
            </div>
          ) : tenants.map(t => {
            const f = safeParseFeatures(t.features);
            const activeFeats = Object.entries(f).filter(([,v]) => v).map(([k]) => k);
            return (
              <div key={t.id} style={{ padding:'14px 16px', borderBottom:'1px solid var(--gray-100)' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, flex:1, minWidth:0 }}>
                    <div style={{ width:36, height:36, borderRadius:8, background:t.primary_color||'#2563eb', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontWeight:800, fontSize:14 }}>
                      {t.logo_url
                        ? <img src={t.logo_url} alt="" style={{ width:36, height:36, borderRadius:8, objectFit:'cover' }} />
                        : (t.name?.[0]||'A')}
                    </div>
                    <div style={{ minWidth:0 }}>
                      <div style={{ fontWeight:700, fontSize:14, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.name}</div>
                      <div style={{ fontSize:11, color:'var(--gray-400)' }}>{t.slug} · {t.user_count} usuário(s)</div>
                    </div>
                  </div>
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4, flexShrink:0 }}>
                    <span className={`badge ${
                      t.status==='active' ? 'badge-success'
                      : t.status==='suspended' ? 'badge-warning'
                      : t.status==='deleted' ? 'badge-gray'
                      : 'badge-info'
                    }`}>
                      {t.status==='active'?'Ativo'
                        : t.status==='suspended'?'Suspenso'
                        : t.status==='deleted'?'Excluído'
                        : 'Trial'}
                    </span>
                    {t.plan_name && <span className="badge badge-info" style={{ fontSize:10 }}>{t.plan_name}</span>}
                  </div>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:10 }}>
                  <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                    {activeFeats.slice(0,4).map(k => (
                      <span key={k} style={{ fontSize:13 }} title={k}>{FEAT_ICONS[k]||'•'}</span>
                    ))}
                    {activeFeats.length > 4 && <span style={{ fontSize:11, color:'var(--gray-400)' }}>+{activeFeats.length-4}</span>}
                  </div>
                  <div style={{ display:'flex', gap:6 }}>
                    <Link to={`/master/tenants/${t.id}/edit`} className="btn btn-xs btn-secondary">Editar</Link>
                    <ImpersonateBtn tenantId={t.id} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
